import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DataTable, DataTableExpandedRows, DataTableFilterMeta, DataTableStateEvent, DataTableValueArray } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { ColumnDTO, downloadFile, ETypeOfNotification, formatDateTime, Modal, setNotification } from 'medere-ui-framework';
import {
    MAILING_REPORT_MESSAGE_STATES,
    MailingReportDateRange,
    MailingReportDetailFilter,
    MailingReportDetailItemDTO,
    MailingReportNodeDTO,
} from '../../dto/MailingReportDTO';
import { exportMailingReportDetail, getMailingReportDetail } from '../../services/MailingReportController';
import { parseApiError } from '../../services/apiError';
import MailingReportMessageStates from './MailingReportMessageStates';

const PAGE_SIZE = 20;

/** Columnas del Excel: los field coinciden con MailingReportDetailItemDTO del backend. */
const EXPORT_COLUMNS: ColumnDTO[] = [
    { field: 'messageTypeDescription', header: 'Tipo de mensaje' },
    { field: 'taskMessageTypeDescription', header: 'Tipo de tarea' },
    { field: 'subject', header: 'Asunto' },
    { field: 'from', header: 'De' },
    { field: 'to', header: 'Para' },
    { field: 'messageStateDescription', header: 'Estado' },
    { field: 'dateChanged', header: 'Cambio de estado' },
    { field: 'dateToSend', header: 'Fecha de envío' },
];

const TEXT_FILTER_FIELDS = ['from', 'to'] as const;
const STATE_FILTER_FIELD = 'messageStateDescription';

const EMPTY_FILTERS: DataTableFilterMeta = {
    from: { value: null, matchMode: 'contains' },
    to: { value: null, matchMode: 'contains' },
    [STATE_FILTER_FIELD]: { value: null, matchMode: 'equals' },
};

function filterValue(filters: DataTableFilterMeta, field: string): string | undefined {
    const meta = filters[field] as { value: string | null } | undefined;
    const value = meta?.value?.trim();
    return value ? value : undefined;
}

function stateFilterValue(filters: DataTableFilterMeta): number | undefined {
    const meta = filters[STATE_FILTER_FIELD] as { value: number | null } | undefined;
    return meta?.value ?? undefined;
}

const stateFilterElement = (options: ColumnFilterElementTemplateOptions) => (
    <Dropdown
        value={options.value ?? null}
        options={MAILING_REPORT_MESSAGE_STATES}
        optionLabel="description"
        optionValue="id"
        placeholder="Todos"
        showClear
        aria-label="Filtrar por estado"
        className="mailing-report__state-filter"
        onChange={(e: DropdownChangeEvent) => options.filterApplyCallback(e.value ?? null)}
    />
);

export function configurationLabel(privateConfiguration: boolean | null): string {
    return privateConfiguration ? 'Configuración privada' : 'Configuración pública';
}

const text = (value: string | null) => value || '—';
const dateTime = (value: number | null) => value ? formatDateTime(value) : '—';

interface MailingReportDetailModalProps {
    node: MailingReportNodeDTO | null;
    path: MailingReportNodeDTO[];
    range: MailingReportDateRange;
    onHide: () => void;
}

function MailingReportDetailModal({ node, path, range, onHide }: MailingReportDetailModalProps) {
    const dispatch = useDispatch();
    const [items, setItems] = useState<MailingReportDetailItemDTO[]>([]);
    const [count, setCount] = useState(0);
    const [first, setFirst] = useState(0);
    const [filters, setFilters] = useState<DataTableFilterMeta>(EMPTY_FILTERS);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray | undefined>(undefined);

    const detailFilter: MailingReportDetailFilter | null = node && {
        ...range,
        privateConfiguration: node.privateConfiguration ?? undefined,
        messageTypeId: node.messageTypeId ?? undefined,
        taskMessageTypeId: node.taskMessageTypeId ?? undefined,
        messageStateId: stateFilterValue(filters),
        from: filterValue(filters, 'from'),
        to: filterValue(filters, 'to'),
    };
    const detailFilterKey = JSON.stringify(detailFilter);

    useEffect(() => {
        setFirst(0);
        setFilters(EMPTY_FILTERS);
        setExpandedRows(undefined);
    }, [node?.key, range.initialDate, range.finalDate]);

    useEffect(() => {
        if (!detailFilter) return;
        let cancelled = false;
        setLoading(true);
        getMailingReportDetail(detailFilter, first, PAGE_SIZE)
            .then(page => {
                if (cancelled) return;
                setItems(page.items);
                setCount(page.count);
            })
            .catch(e => {
                if (cancelled) return;
                setItems([]);
                setCount(0);
                dispatch(setNotification({
                    title: 'No se pudo cargar el detalle del reporte',
                    desc: parseApiError(e).message,
                    icon: 'pi-times',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [detailFilterKey, first]);

    async function handleExport() {
        if (!detailFilter) return;
        setExporting(true);
        try {
            await downloadFile(await exportMailingReportDetail(detailFilter, EXPORT_COLUMNS), 'ReporteDeMensajeria.xlsx');
        } catch {
            dispatch(setNotification({
                title: 'No se pudo exportar el detalle. Intentá de nuevo.',
                desc: '',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setExporting(false);
        }
    }

    function clearFilters() {
        setFilters(EMPTY_FILTERS);
        setFirst(0);
    }

    function toggleHistory(row: MailingReportDetailItemDTO) {
        const expanded = (expandedRows as DataTableExpandedRows | undefined) ?? {};
        setExpandedRows(expanded[row.messageId] ? {} : { [row.messageId]: true });
    }

    const header = node
        ? ['Detalle', configurationLabel(path[0]?.privateConfiguration ?? node.privateConfiguration),
            ...path.slice(1).map(p => p.description ?? '—')].join(' · ')
        : '';

    const hasFilters = TEXT_FILTER_FIELDS.some(field => filterValue(filters, field)) || stateFilterValue(filters) !== undefined;

    return (
        <Modal visible={!!node} setVisible={visible => { if (!visible) onHide(); }} onHideCustom={onHide}
            header={header} width="w-2xlarge" closable
            className="mailing-report__dialog" contentClassName="mailing-report__dialog-content">
            <div className="mailing-report__detail">
                <div className="mailing-report__actions-row">
                    <span className="mailing-report__count">
                        {count === 1 ? '1 mensaje' : `${count} mensajes`}
                    </span>
                    <Button className="buttonMain2 mailing-report__export" outlined icon="pi pi-file-excel" label="Exportar"
                        loading={exporting} onClick={handleExport} />
                </div>

                <DataTable
                    value={items}
                    dataKey="messageId"
                    lazy
                    paginator
                    rows={PAGE_SIZE}
                    first={first}
                    totalRecords={count}
                    onPage={(e: DataTableStateEvent) => setFirst(e.first)}
                    filters={filters}
                    filterDisplay="row"
                    filterDelay={400}
                    onFilter={(e: DataTableStateEvent) => { setFilters(e.filters); setFirst(0); }}
                    loading={loading}
                    expandedRows={expandedRows}
                    rowExpansionTemplate={(row: MailingReportDetailItemDTO) => <MailingReportMessageStates messageId={row.messageId} />}
                    className="mailing-report__table"
                    scrollable
                    scrollHeight="flex"
                    emptyMessage={hasFilters
                        ? <>No hay resultados para estos filtros. <Button link label="Limpiar filtros" onClick={clearFilters} /></>
                        : 'No hay mensajes para este rango de fechas.'}
                >
                    <Column header="Tipo de mensaje" body={(row: MailingReportDetailItemDTO) => text(row.messageTypeDescription)} />
                    <Column header="Tipo de tarea" body={(row: MailingReportDetailItemDTO) => text(row.taskMessageTypeDescription)} />
                    <Column header="Asunto" className="mailing-report__cell-truncate" body={(row: MailingReportDetailItemDTO) =>
                        <span title={row.subject ?? undefined}>{text(row.subject)}</span>} />
                    <Column field="from" header="De" filter showFilterMenu={false} filterPlaceholder="Buscar"
                        body={(row: MailingReportDetailItemDTO) => text(row.from)} />
                    <Column field="to" header="Para" filter showFilterMenu={false} filterPlaceholder="Buscar"
                        body={(row: MailingReportDetailItemDTO) => text(row.to)} />
                    <Column field={STATE_FILTER_FIELD} header="Estado" filter showFilterMenu={false} showClearButton={false}
                        filterElement={stateFilterElement}
                        body={(row: MailingReportDetailItemDTO) => text(row.messageStateDescription)} />
                    <Column header="Cambio de estado" className="mailing-report__cell-center" headerClassName="mailing-report__cell-center"
                        body={(row: MailingReportDetailItemDTO) => dateTime(row.dateChanged)} />
                    <Column header="Fecha de envío" className="mailing-report__cell-center" headerClassName="mailing-report__cell-center"
                        body={(row: MailingReportDetailItemDTO) => dateTime(row.dateToSend)} />
                    <Column className="mailing-report__cell-action" filter showFilterMenu={false}
                        filterElement={() => (
                            <Button rounded className="nv-control" icon="pi pi-eraser" aria-label="Limpiar filtros"
                                tooltip="Limpiar filtros" tooltipOptions={{ event: 'both', position: 'left', showOnDisabled: true }}
                                onClick={clearFilters} />
                        )}
                        body={(row: MailingReportDetailItemDTO) => (
                        <Button text className="nv-icon" icon="pi pi-history" aria-label="Ver historial"
                            tooltip="Ver historial" tooltipOptions={{ event: 'both', position: 'left' }}
                            onClick={() => toggleHistory(row)} />
                    )} />
                </DataTable>
            </div>
        </Modal>
    );
}

export default MailingReportDetailModal;
